/**
 * THE SHAPE A CHAT MESSAGE TAKES IN THE DATABASE.
 *
 * Pure. Extracted out of `useChatSync` so the invariant below can be tested
 * without React, Redux, Supabase or an auth context in the import graph — which
 * is not a tidiness argument: `ui-frontend` is a pnpm project whose CI cannot
 * run a full install (see `.github/workflows/ui-frontend-browser-e2e.yml`), so a
 * test that drags the hook's dependencies can only ever run on a developer's
 * machine. This module's entire runtime graph is empty, so its test runs in CI.
 *
 * ⚠️ THE INVARIANT: `message` IS THE TEXT EXACTLY AS SENT, AND A TRANSLATION IS
 * ADDED BESIDE IT — NEVER OVER IT.
 *
 * `chat_messages.content` is untyped `jsonb` with no CHECK constraint
 * (`supabase/init_new_env.sql:9287`), which is why adding the translation needed
 * no migration — and by exactly the same token nothing in the database stops a
 * later writer dropping the original. `chatMessagePersistence.test.ts` is the
 * only thing that does.
 *
 * It matters because this row outlives the call: the archive writes it to
 * `participant-chat.txt` (`app/api/archive-call/route.ts:1173-1197`, which
 * renders ONE field) and the incident report reads it
 * (`app/api/incident-report/[callId]/route.ts:45`). The measured failures on
 * translated output are `我在十八楼二零三室` ("room 203") arriving as "room
 * 2030" and `门牌号` ("house number") as "license plate" — a wrong address and a
 * wrong vehicle. Written over the original, those become a record of words
 * nobody typed.
 */

import type { ParticipantChatMessage } from '@/store/slices/participantChatSlice';

/** Database schema for chat message content. */
export interface DbChatMessage {
  id: string;
  type: 'officer' | 'participant';
  sender_name: string;
  sender_identity: string;
  /** ⚠️ THE MESSAGE EXACTLY AS SENT. NEVER a translation. See the module note. */
  message: string;
  timestamp_ms: number;
  is_officer?: boolean;
  tps_results?: unknown; // Officer-specific TPS search results
  /**
   * The translation the OFFICER read, alongside the original — the same
   * "what they said, vs what was translated" pairing the transcripts keep.
   *
   * ⚠️ ONE SIDE ONLY. Only the officer's browser persists chat
   * (`enabled: isOfficer`), so what the CALLER was shown has no writer. Recorded
   * rather than papered over: `anon` is REVOKED on `chat_messages`, so a caller
   * write path is not a small change.
   */
  translation?: string;
  /** The language `translation` is in — never inferred by a reader. */
  translation_language?: string;
  /** The SENDER's call-assigned language code off the wire (rule 42); absent when unassigned. */
  sender_language?: string;
  /**
   * `'wire'`: `timestamp_ms` is the sender's send time (rule 49). `'arrival'`:
   * the frame carried no envelope and this browser stamped it on receipt.
   */
  timestamp_source?: 'wire' | 'arrival';
}

/** The per-message translation state a browser holds, keyed by message id. */
export type ChatTranslationSnapshot = Readonly<
  Record<string, { state: string; targetLanguage: string; text: string | null } | undefined>
>;

/**
 * Convert a Redux `ParticipantChatMessage` to the DB shape.
 *
 * The translation is ADDED, never substituted: `message` keeps the original
 * under every code path here.
 */
export function participantToDbFormat(
  msg: ParticipantChatMessage,
  translations: ChatTranslationSnapshot,
): DbChatMessage {
  const translation = translations[msg.id];
  const row: DbChatMessage = {
    id: msg.id,
    type: 'participant',
    sender_name: msg.from.name,
    sender_identity: msg.from.identity,
    message: msg.message,
    timestamp_ms: msg.timestamp,
    is_officer: msg.from.isOfficer,
  };
  if (typeof msg.senderLanguage === 'string') row.sender_language = msg.senderLanguage;
  if (msg.timestampSource) row.timestamp_source = msg.timestampSource;
  // Only a real translation is recorded. A pending or failed one is not a
  // finding, and writing an empty string would later read as "this said
  // nothing" — a claim about the speaker rather than about the translator.
  if (translation && translation.state === 'translated' && translation.text) {
    row.translation = translation.text;
    row.translation_language = translation.targetLanguage;
  }
  return row;
}
